import { useEffect, useMemo, useRef, useState } from 'react'
import { useBlocker } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { SliceHealthStatus, SliceStatusError } from '@ui/__generated__/graphql'
import { ActionModal } from '@ui/components/core'
import { DatableLog, DataViewerProps, PanelEnum, StartAndEnd } from '@ui/interfaces'
import { getStartAndEnd } from '@ui/lib/helpers/timeWindow'
import { LinechartAndPipelineQuery, SimulateEventPipelineQuery } from '@ui/loaders/processor'
import { Code } from '@ui/pages/pipelinesWorkflow/components/Code/Code'
import { DataViewer } from '@ui/pages/pipelinesWorkflow/components/DataViewer'
import { SaveToExistingPipeline } from '@ui/pages/pipelinesWorkflow/components/Save/SaveToExistingPipeline'
import { SaveToNewOrExistingPipeline } from '@ui/pages/pipelinesWorkflow/components/Save/SaveToNewOrExistingPipeline'
import { TopBar } from '@ui/pages/pipelinesWorkflow/components/TopBar'
import { useCodeNodeStore, useNodeAndEdgeStore, useTabSelectorStore, useTelemetryStore, useTimeStore } from '@ui/stores'
import { CategoryScale } from 'chart.js'
import { Chart as ChartJS } from 'chart.js/auto'

ChartJS.register(CategoryScale)

interface TransformationEditorProps {
  create?: boolean
}

export const Transform = ({ create = false }: TransformationEditorProps) => {
  const isSelectedEventSet = useRef(false)

  const {
    setDisplayModal,
    loafId: pipelineId,
    currentNodeId,
    previouslySavedCode,
    previouslySavedDescription,
    setClosePanel,
    nodes,
    edges,
  } = useNodeAndEdgeStore(
    useShallow((state) => ({
      setDisplayModal: state.setDisplayModal,
      loafId: state.loafId,
      currentNodeId: state.currentNodeId as string,
      previouslySavedCode: state.getCurrentNodeKey('code'),
      previouslySavedDescription: state.getCurrentNodeKey('description'),
      setClosePanel: state.setClosePanel,
      nodes: state.nodes,
      edges: state.edges,
    }))
  )

  const { code, setCode, setDescription, setConsoleLog, setErrorLog } = useCodeNodeStore(
    useShallow((state) => ({
      table: state.table,
      setTable: state.setTable,
      code: state.code,
      setCode: state.setCode,
      setDescription: state.setDescription,
      setConsoleLog: state.setConsoleLog,
      setErrorLog: state.setErrorLog,
      diffIndex: state.diffIndex,
      whereClause: state.whereClause,
    }))
  )

  const { whereClause, dataSource, setDataSource, selectedEvent, setSelectedEvent, logs, spans } = useTelemetryStore(
    useShallow((state) => ({
      dataSource: state.dataSource,
      setDataSource: state.setDataSource,
      whereClause: state.whereClause,
      selectedEvent: state.selectedEvent,
      setSelectedEvent: state.setSelectedEvent,
      logs: state.logs,
      spans: state.spans,
    }))
  )

  const { setTabs, setCurrentTab, currentTab } = useTabSelectorStore(
    useShallow((state) => ({
      setTabs: state.setTabs,
      setCurrentTab: state.setCurrentTab,
      currentTab: state.currentTab,
    }))
  )

  // const timewindow = useTimeStore((state) => state.timewindow)
  // const { startTime, endTime } = getInAbsoluteTime(timewindow)

  const timewindow = useTimeStore(useShallow((state) => state.timewindow))

  const [openModal, setOpenModal] = useState(false)
  const [editorModified, setEditorModified] = useState(false)
  const [saveTabOpen, setSaveTabOpen] = useState(false)
  const [localCode, setLocalCode] = useState('')
  const [startAndEnd, setStartAndEnd] = useState<StartAndEnd>(getStartAndEnd(timewindow))

  // we only want to use the edit params if we are editing a node from
  // the mimnimap
  // const useEditParams = !!editSearchParam

  const { data, loading } = LinechartAndPipelineQuery(
    {
      pipelineId: pipelineId,
      currentNodeId: currentNodeId as string,
      codeChain: [
        {
          nodeId: currentNodeId as string,
          code: code ?? '',
        },
      ],
    },
    {
      from: dataSource,
      limit: 100,
      offset: 0,
      where: whereClause,
      ...startAndEnd,
    }
  )

  const { data: executionData } = SimulateEventPipelineQuery({
    selectedNodeId: currentNodeId,
    selectedNodeCode: localCode,
    selectedEvent: selectedEvent || ({} as DatableLog),
    selectedEventType: dataSource === 'logs' ? 'log' : 'span',
    events: dataSource === 'logs' ? logs : spans,
    nodes: nodes,
    edges: edges,
    queryParams: {
      from: dataSource,
      limit: 100,
      offset: 0,
      where: whereClause,
      ...startAndEnd,
    },
    skip: !data,
  })

  const [nodeStatus, setNodeStatus] = useState<{
    logsStatus: SliceHealthStatus
    spansStatus: SliceHealthStatus
    metricsStatus: SliceHealthStatus
  }>({
    logsStatus: SliceHealthStatus.Disabled,
    spansStatus: SliceHealthStatus.Disabled,
    metricsStatus: SliceHealthStatus.Disabled,
  })
  const [nodeEnabled, setNodeEnabled] = useState<{
    logsEnabled: boolean
    spansEnabled: boolean
    metricsEnabled: boolean
  }>({
    logsEnabled: false,
    spansEnabled: false,
    metricsEnabled: false,
  })

  const [nodeErrors, setNodeErrors] = useState<SliceStatusError[]>([])
  const nodeStatusData = nodes.find((node) => node.id === currentNodeId)?.data.status

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => editorModified && currentLocation.pathname !== nextLocation.pathname
  )

  const cancelTransform = (allowCancel?: boolean) => {
    if (editorModified && !allowCancel) {
      setOpenModal(true)
    } else {
      setCode('')
      setDescription('')
      setConsoleLog('')
      setErrorLog('')
      setSaveTabOpen(false)
      setDisplayModal(PanelEnum.HIDDEN)
    }
  }

  // on enter
  useEffect(() => {
    setStartAndEnd(getStartAndEnd(timewindow))
    setTabs(['Input', 'Code', 'Output'])
    setCurrentTab('Code')
    setDataSource('logs')
    setCode(previouslySavedCode as string)
    setDescription((previouslySavedDescription as string) || '')
  }, [])

  // on exit
  useEffect(() => {
    setClosePanel(() => cancelTransform(false))
  }, [setClosePanel, setCode, setConsoleLog, setDescription, setDisplayModal, setErrorLog])

  const dataViewerProps: DataViewerProps = useMemo(() => {
    return {
      data: executionData?.pipeline?.simulateEvent?.events || [],
      isLoading: loading,
      lineChart: data?.pipeline?.lineChart,
    }
  }, [executionData, data, loading])

  useEffect(() => {
    if (currentTab === 'Code' && localCode !== code) {
      setCode(localCode)
    }
  }, [code, currentTab, localCode, setCode])

  // if selected event is empty, set it to the first event in the data
  useEffect(() => {
    console.log('executionData', executionData)
    if (!isSelectedEventSet.current && executionData?.pipeline?.simulateEvent?.events.length) {
      setSelectedEvent(executionData?.pipeline?.simulateEvent?.events[0].input)
      isSelectedEventSet.current = true
    }
  }, [executionData, setSelectedEvent])

  useEffect(() => {
    if (nodeStatusData) {
      const { logsStatus, logsEnabled, spansStatus, spansEnabled, metricsStatus, metricsEnabled, errors } =
        nodeStatusData
      setNodeStatus({
        logsStatus: logsStatus,
        spansStatus: spansStatus,
        metricsStatus: metricsStatus,
      })
      setNodeEnabled({
        logsEnabled: logsEnabled,
        spansEnabled: spansEnabled,
        metricsEnabled: metricsEnabled,
      })
      setNodeErrors(errors as SliceStatusError[])
    }
  }, [nodeStatusData])

  return (
    <div className={'flex h-full w-full max-w-full flex-col'}>
      <ActionModal
        open={blocker.state === 'blocked' && editorModified && !openModal}
        title={'Save changes?'}
        message={'You have unsaved changes. Do you want to save them?'}
        handleAction={() => {
          setSaveTabOpen(true)
          setOpenModal(false)
          if (blocker.reset) {
            blocker.reset()
          }
        }}
        actionIdentifier={'Save'}
        dismissAction={() => {
          cancelTransform(true)
          if (blocker.proceed) {
            blocker.proceed()
          }
        }}
        dismissIdentifier={'Continue'}
        loading={false}
      />
      {!saveTabOpen && (
        <TopBar
          loading={loading}
          setParams={() => ({})} // setParams}
          setSaveTabOpen={(value: boolean) => setSaveTabOpen(value)}
          cancelTransform={cancelTransform}
          nodeStatus={nodeStatus}
          nodeEnabled={nodeEnabled}
          nodeErrors={nodeErrors}
        />
      )}
      {currentTab == 'Input' && !saveTabOpen && <DataViewer {...dataViewerProps} tableType="input" />}
      {currentTab == 'Code' && !saveTabOpen && (
        <Code
          selectedEvent={selectedEvent ? selectedEvent : {}}
          events={executionData?.pipeline?.simulateEvent || []}
          setParentCode={setLocalCode}
          setEditorModified={setEditorModified}
        />
      )}
      {currentTab == 'Output' && !saveTabOpen && <DataViewer {...dataViewerProps} tableType="output" />}

      {!create
        ? saveTabOpen && <SaveToExistingPipeline setSaveTabClosed={() => setSaveTabOpen(false)} />
        : saveTabOpen && <SaveToNewOrExistingPipeline setSaveTabClosed={() => setSaveTabOpen(false)} />}
    </div>
  )
}
